/**
 * One request per data need: GETs that several widgets ask for at the
 * same time share a single in-flight promise.
 *
 * Why (owner, 2026-10-07): the signed-in home fired /api/properties and
 * /api/watchlist twice (the alerts widget and the pins/properties
 * stores), /api/auth/onboarding twice (the page and the roadmap) and
 * the same ppm-distribution three times (one per pulse chart). Each
 * widget is right to ask; they just must not each pay for it.
 *
 * This is a de-duplicator, not a cache: a settled answer is reused only
 * for `reuseMs` (default 2 s, long enough for widgets that mount a beat
 * apart and for React Strict Mode's second mount in dev), so nothing
 * goes stale. After a write, pass `fresh: true` (or call
 * invalidateShared) to skip the shared answer.
 */
import { API_BASE } from "@/lib/api";

type Entry = { promise: Promise<unknown>; settledAt: number | null };

const entries = new Map<string, Entry>();

/** Answers a combined read carries for a key (seedShared). */
type Seed = { source: Promise<unknown>; settledAt: number | null };
const seeds = new Map<string, Seed>();

/** Lets one combined read answer ``key`` (the hub's bootstrap, lib/
 *  hub-boot, carries the onboarding flags and the feedback offer): a
 *  caller that asks while it is in flight, or within the caller's reuse
 *  window after it lands, gets its answer instead of a request of its
 *  own. When it has no answer (``undefined``: signed out, an error), the
 *  caller's own load runs then, and only then. */
export function seedShared<T>(key: string, source: Promise<T | undefined>, now: () => number = Date.now): void {
  const seed: Seed = { source, settledAt: null };
  source.then(() => { seed.settledAt = now(); }, () => { seed.settledAt = now(); });
  seeds.set(key, seed);
  entries.delete(key);
}

export interface SharedGetOptions {
  /** How long a settled answer may be handed out again. */
  reuseMs?: number;
  /** Skip any shared answer and replace it with a new request. */
  fresh?: boolean;
  /** Injected for tests; defaults to the global fetch. */
  fetcher?: typeof fetch;
  /** Injected for tests; defaults to Date.now. */
  now?: () => number;
}

/** Run `load` once per `key` while it is in flight (and for `reuseMs`
 *  after it settles); every caller in that window gets the same
 *  promise. A rejected load is never shared afterwards. */
export function shared<T>(key: string, load: () => Promise<T>, opts: SharedGetOptions = {}): Promise<T> {
  const { reuseMs = 2000, fresh = false, now = Date.now } = opts;
  const hit = entries.get(key);
  if (hit && !fresh && (hit.settledAt === null || now() - hit.settledAt <= reuseMs)) {
    return hit.promise as Promise<T>;
  }
  const seed = seeds.get(key);
  seeds.delete(key);
  const seeded = !!seed && !fresh && (seed.settledAt === null || now() - seed.settledAt <= reuseMs);
  const run = seeded
    ? () => seed!.source.then((v) => (v === undefined ? load() : (v as T)), () => load())
    : load;
  const entry: Entry = { promise: Promise.resolve(), settledAt: null };
  entry.promise = run().then(
    (v) => {
      entry.settledAt = now();
      return v;
    },
    (e) => {
      if (entries.get(key) === entry) entries.delete(key);
      throw e;
    },
  );
  entries.set(key, entry);
  return entry.promise as Promise<T>;
}

/** GET `${API_BASE}${path}` as JSON with the session cookie, shared by
 *  path. Throws on a non-2xx status. */
export function sharedGet<T>(path: string, opts: SharedGetOptions = {}): Promise<T> {
  const fetcher = opts.fetcher ?? fetch;
  return shared<T>(path, () => fetcher(`${API_BASE}${path}`, { credentials: "include" }).then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json() as Promise<T>;
  }), opts);
}

/** @public (the unit tests exercise it directly)
 * Forget shared answers whose path starts with `prefix` ("" = all),
 *  e.g. after a write or a sign-out. */
export function invalidateShared(prefix = ""): void {
  for (const k of [...entries.keys()]) if (k.startsWith(prefix)) entries.delete(k);
  for (const k of [...seeds.keys()]) if (k.startsWith(prefix)) seeds.delete(k);
}
